import { useState } from 'react'
import { Container, Row, Col, Card, Button, Table, Badge, Modal, Form, Alert, Spinner } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'
import ClientForm from '../../components/Clients/ClientForm'
import ClientDetail from '../../components/Clients/ClientDetail'
import ContactForm from '../../components/Clients/ContactForm'
import DocumentForm from '../../components/Clients/DocumentForm'
import CarteraBulkUploadModal from '../../components/Clients/CarteraBulkUploadModal'
import { CertIndicadorDot } from '../../components/common/CertIndicadorDot'
import { CERT_INDICADOR_LABELS } from '../../constants/certIndicador'
import './Clients.css'

const Clients = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [showDetail, setShowDetail] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [showDocumentForm, setShowDocumentForm] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [selectedContact, setSelectedContact] = useState(null)
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterEstado, setFilterEstado] = useState('all')
  const [orderBy, setOrderBy] = useState('business_name')
  const [filterCodigoPostal, setFilterCodigoPostal] = useState('')
  const [formErrors, setFormErrors] = useState({})
  const [showBulkModal, setShowBulkModal] = useState(false)

  const canBulkUpload = ['admin', 'superadmin', 'contador'].includes(user?.role) || user?.is_superuser

  // Obtener lista de clientes
  const { data: clientsData, isLoading, error } = useQuery(
    ['clients', user?.id, searchTerm, filterEstado, orderBy, filterCodigoPostal],
    async () => {
      const params = new URLSearchParams()
      if (searchTerm) params.append('search', searchTerm)
      if (filterEstado !== 'all') params.append('estado', filterEstado)
      if (orderBy) params.append('ordering', orderBy)
      if (filterCodigoPostal) params.append('codigo_postal', filterCodigoPostal)

      const response = await api.get(`/clients/clients/?${params.toString()}`)
      return response.data.results || response.data || []
    },
    { enabled: !!user, staleTime: 0 }
  )

  // Mutación para crear/actualizar cliente
  const clientMutation = useMutation(
    async (clientData) => {
      console.log('clientMutation - Datos recibidos:', clientData)
      if (clientData.id) {
        console.log('Actualizando cliente ID:', clientData.id)
        return api.put(`/clients/clients/${clientData.id}/`, clientData)
      } else {
        console.log('Creando nuevo cliente')
        return api.post('/clients/clients/', clientData)
      }
    },
    {
      onSuccess: (response, clientData) => {
        console.log('Cliente guardado exitosamente:', response.data)
        queryClient.invalidateQueries('clients')
        setShowForm(false)
        const isUpdate = !!clientData.id
        setSelectedClient(null)
        setFormErrors({}) // Limpiar errores al cerrar
        toast.success(isUpdate ? 'Cliente actualizado exitosamente' : 'Cliente creado exitosamente')
      },
      onError: (error) => {
        // Si hay errores de validación del backend, pasarlos al formulario
        if (error.response?.data && typeof error.response.data === 'object') {
          const validationErrors = error.response.data
          const formattedErrors = {}
          
          // Formatear errores para el formulario
          Object.entries(validationErrors).forEach(([field, messages]) => {
            if (field === '__all__') {
              // Errores generales (como CURP inválido) se muestran en el campo correspondiente
              const msg = Array.isArray(messages) ? messages[0] : messages
              if (msg.includes('CURP')) {
                formattedErrors.curp = msg
              } else if (msg.includes('contador')) {
                formattedErrors.contador = msg
              } else {
                toast.error(msg)
              }
            } else {
              formattedErrors[field] = Array.isArray(messages) ? messages[0] : messages
            }
          })
          
          setFormErrors(formattedErrors)
          
          // Mostrar mensaje general
          const errorMessages = Object.entries(validationErrors)
            .filter(([field]) => field !== '__all__')
            .map(([field, messages]) => {
              const msg = Array.isArray(messages) ? messages.join(', ') : messages
              return `${field}: ${msg}`
            })
            .join('; ')
          
          if (errorMessages) {
            toast.error(`Error de validación: ${errorMessages}`)
          }
        } else {
          const message = error.response?.data?.error || error.response?.data?.message || 'Error al guardar cliente'
          toast.error(message)
        }
      }
    }
  )

  // Mutación para eliminar cliente
  const deleteMutation = useMutation(
    async (clientId) => {
      return api.delete(`/clients/clients/${clientId}/`)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('clients')
        toast.success('Cliente eliminado exitosamente')
      },
      onError: (_error) => {
        toast.error('Error al eliminar cliente')
      }
    }
  )

  const handleCreate = () => {
    setSelectedClient(null)
    setShowForm(true)
  }

  const handleEdit = async (client) => {
    // Obtener el detalle completo del cliente para asegurar que todos los campos estén disponibles
    try {
      const response = await api.get(`/clients/clients/${client.id}/`)
      setSelectedClient(response.data)
      setShowForm(true)
    } catch (error) {
      console.error('Error al cargar detalle del cliente:', error)
      // Si falla, usar el cliente de la lista de todos modos
      setSelectedClient(client)
      setShowForm(true)
    }
  }

  const handleView = async (client) => {
    try {
      const response = await api.get(`/clients/clients/${client.id}/`)
      setSelectedClient(response.data)
    } catch {
      setSelectedClient(client)
    }
    setShowDetail(true)
  }

  const handleDelete = (client) => {
    if (window.confirm(`¿Estás seguro de eliminar al cliente ${client.razon_social || client.business_name}?`)) {
      deleteMutation.mutate(client.id)
    }
  }

  const handleSubmit = (formData) => {
    clientMutation.mutate(formData)
  }

  // Mutación para contactos
  const contactMutation = useMutation(
    async (contactData) => {
      if (contactData.id) {
        return api.put(`/clients/contacts/${contactData.id}/`, contactData)
      } else {
        return api.post('/clients/contacts/', contactData)
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-contacts', selectedClient?.id])
        setShowContactForm(false)
        setSelectedContact(null)
        toast.success(selectedContact ? 'Contacto actualizado exitosamente' : 'Contacto creado exitosamente')
      },
      onError: (error) => {
        const data = error.response?.data
        let message = data?.error || data?.message || 'Error al guardar contacto'
        if (typeof data === 'object' && !data.error && !data.message) {
          const details = Object.entries(data)
            .filter(([, v]) => v && (Array.isArray(v) ? v.length : true))
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`)
          if (details.length) message = details.join('; ')
        }
        toast.error(message)
      }
    }
  )

  // Mutación para documentos
  const documentMutation = useMutation(
    async (documentData) => {
      const isFormData = documentData instanceof FormData
      const id = isFormData ? documentData.get('id') : documentData?.id
      if (id) {
        const url = `/clients/documents/${id}/`
        if (isFormData) {
          documentData.delete('id')
          return api.put(url, documentData)
        }
        const { id: _id, ...payload } = documentData
        return api.put(url, payload)
      }
      return api.post('/clients/documents/', documentData)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-documents', selectedClient?.id])
        setShowDocumentForm(false)
        setSelectedDocument(null)
        toast.success(selectedDocument ? 'Documento actualizado exitosamente' : 'Documento creado exitosamente')
      },
      onError: (error) => {
        const data = error.response?.data
        let message = data?.error || data?.message || 'Error al guardar documento'
        if (typeof data === 'object' && !data.error && !data.message) {
          const details = Object.entries(data)
            .filter(([, v]) => v && (Array.isArray(v) ? v.length : true))
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`)
          if (details.length) message = details.join('; ')
        }
        toast.error(message)
      }
    }
  )

  const handleCreateContact = () => {
    if (!selectedClient) {
      toast.error('Debes seleccionar un cliente primero')
      return
    }
    setSelectedContact(null)
    setShowContactForm(true)
  }

  const handleCreateDocument = () => {
    if (!selectedClient) {
      toast.error('Debes seleccionar un cliente primero')
      return
    }
    setSelectedDocument(null)
    setShowDocumentForm(true)
  }

  const handleEditContact = (contact) => {
    setSelectedContact(contact)
    setShowContactForm(true)
  }

  const handleEditDocument = (document) => {
    setSelectedDocument(document)
    setShowDocumentForm(true)
  }

  const handleSubmitContact = (formData) => {
    contactMutation.mutate(formData)
  }

  const handleSubmitDocument = (formData) => {
    documentMutation.mutate(formData)
  }

  const clients = clientsData || []

  return (
    <Container fluid className="clients-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="mb-1">Cartera</h1>
          <div className="text-muted small d-flex flex-wrap gap-3 align-items-center">
            <span className="d-inline-flex align-items-center gap-1">
              <CertIndicadorDot indicador="ok" size={8} /> Vigente
            </span>
            <span className="d-inline-flex align-items-center gap-1">
              <CertIndicadorDot indicador="warning" size={8} /> ≤45 días
            </span>
            <span className="d-inline-flex align-items-center gap-1">
              <CertIndicadorDot indicador="danger" size={8} /> ≤20 días / vencido
            </span>
            <span className="d-inline-flex align-items-center gap-1">
              <CertIndicadorDot indicador="none" size={8} /> Sin firma
            </span>
          </div>
        </div>
        <div className="d-flex gap-2">
          {canBulkUpload && (
            <Button variant="outline-primary" onClick={() => setShowBulkModal(true)}>
              Carga masiva
            </Button>
          )}
          <Button variant="primary" onClick={handleCreate}>
            + Nuevo Cliente
          </Button>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-2">
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Buscar por nombre, RFC, email, clave, CP..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={filterEstado}
                onChange={(e) => setFilterEstado(e.target.value)}
              >
                <option value="all">Todos</option>
                <option value="activo">Activos</option>
                <option value="inactivo">Inactivos</option>
                <option value="suspendido">Suspendidos</option>
                <option value="baja">Dados de baja</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Control
                type="text"
                placeholder="Código postal"
                value={filterCodigoPostal}
                onChange={(e) => setFilterCodigoPostal(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Form.Select
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
              >
                <option value="business_name">Orden: Nombre</option>
                <option value="-business_name">Orden: Nombre (Z-A)</option>
                <option value="clave_prefijo">Orden: Clave</option>
                <option value="-clave_prefijo">Orden: Clave (desc)</option>
                <option value="codigo_postal">Orden: CP</option>
                <option value="-codigo_postal">Orden: CP (desc)</option>
                <option value="regimen_fiscal__codigo">Orden: Régimen fiscal</option>
                <option value="-regimen_fiscal__codigo">Orden: Régimen (desc)</option>
                <option value="rfc">Orden: RFC</option>
              </Form.Select>
            </Col>
            <Col md={2} className="text-end">
              <Badge bg="secondary">Total: {clients.length}</Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Lista de clientes */}
      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger">
          Error al cargar clientes. Por favor, intenta nuevamente.
        </Alert>
      ) : clients.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-muted">No se encontraron clientes</p>
            <Button variant="primary" onClick={handleCreate}>
              Crear primer cliente
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Clave</th>
                  <th>RFC</th>
                  <th>Nombre / Razón Social</th>
                  <th title="FIEL / e.firma">FIEL</th>
                  <th title="Certificado de sello digital (facturación)">CSD</th>
                  <th>Tipo</th>
                  <th>Régimen fiscal</th>
                  <th>Código postal</th>
                  <th>Cuenta para cobros</th>
                  <th>Cuenta contable activo</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.clave_display || '—'}</td>
                    <td>{client.rfc}</td>
                    <td>
                      {client.type_tax_regime === 'fisica' || client.type_name === 'Persona Física'
                        ? `${client.nombre || ''} ${client.apellido_paterno || ''} ${client.apellido_materno || ''}`.trim() || 'Sin nombre'
                        : client.razon_social || client.business_name || 'Sin razón social'
                      }
                    </td>
                    <td className="text-center">
                      <CertIndicadorDot
                        indicador={client.fiel_indicador}
                        title={`FIEL: ${CERT_INDICADOR_LABELS[client.fiel_indicador] || CERT_INDICADOR_LABELS.none}${client.fiel_vigente_hasta ? ` · hasta ${client.fiel_vigente_hasta}` : ''}`}
                      />
                    </td>
                    <td className="text-center">
                      <CertIndicadorDot
                        indicador={client.csd_indicador}
                        title={`CSD: ${CERT_INDICADOR_LABELS[client.csd_indicador] || CERT_INDICADOR_LABELS.none}${client.csd_vigente_hasta ? ` · hasta ${client.csd_vigente_hasta}` : ''}`}
                      />
                    </td>
                    <td>
                      <Badge bg="info">{client.type_name || client.type_tax_regime || 'N/A'}</Badge>
                    </td>
                    <td>{client.regimen_fiscal_codigo || '—'}</td>
                    <td>{client.codigo_postal || '—'}</td>
                    <td><small>{client.cuenta_para_cobros || '—'}</small></td>
                    <td><small>{client.cuenta_contable_activo_display || '—'}</small></td>
                    <td>{client.email || 'N/A'}</td>
                    <td>{client.telefono || 'N/A'}</td>
                    <td>
                      <Badge bg={
                        client.estado === 'activo' ? 'success' :
                        client.estado === 'inactivo' ? 'secondary' :
                        client.estado === 'suspendido' ? 'warning' :
                        client.estado === 'baja' ? 'danger' :
                        client.is_active ? 'success' : 'secondary'
                      }>
                        {client.estado === 'activo' ? 'Activo' :
                         client.estado === 'inactivo' ? 'Inactivo' :
                         client.estado === 'suspendido' ? 'Suspendido' :
                         client.estado === 'baja' ? 'Baja' :
                         client.is_active ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </td>
                    <td>
                      <div className="btn-group" role="group">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleView(client)}
                        >
                          Ver
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleEdit(client)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(client)}
                          disabled={deleteMutation.isLoading}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario */}
      <Modal show={showForm} onHide={() => { setShowForm(false); setSelectedClient(null); setFormErrors({}) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{selectedClient ? 'Editar Cliente' : 'Nuevo Cliente'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ClientForm
            client={selectedClient}
            onSubmit={handleSubmit}
            onCancel={() => { setShowForm(false); setSelectedClient(null); setFormErrors({}) }}
            isLoading={clientMutation.isLoading}
            apiErrors={formErrors}
          />
        </Modal.Body>
      </Modal>

      {/* Modal de detalle */}
      <Modal show={showDetail} onHide={() => { setShowDetail(false); setSelectedClient(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detalle del Cliente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <ClientDetail
              client={selectedClient}
              onEdit={() => {
                setShowDetail(false)
                handleEdit(selectedClient)
              }}
              onCreateContact={handleCreateContact}
              onCreateDocument={handleCreateDocument}
              onEditContact={handleEditContact}
              onEditDocument={handleEditDocument}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Modal de contacto */}
      <Modal show={showContactForm} onHide={() => { setShowContactForm(false); setSelectedContact(null) }}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedContact ? 'Editar Contacto' : 'Nuevo Contacto'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <ContactForm
              clienteId={selectedClient.id}
              contact={selectedContact}
              onSubmit={handleSubmitContact}
              onCancel={() => { setShowContactForm(false); setSelectedContact(null) }}
              isLoading={contactMutation.isLoading}
            />
          )}
        </Modal.Body>
      </Modal>

      {/* Modal de documento */}
      <Modal show={showDocumentForm} onHide={() => { setShowDocumentForm(false); setSelectedDocument(null) }}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedDocument ? 'Editar Documento' : 'Nuevo Documento'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedClient && (
            <DocumentForm
              clienteId={selectedClient.id}
              document={selectedDocument}
              onSubmit={handleSubmitDocument}
              onCancel={() => { setShowDocumentForm(false); setSelectedDocument(null) }}
              isLoading={documentMutation.isLoading}
            />
          )}
        </Modal.Body>
      </Modal>

      <CarteraBulkUploadModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
      />
    </Container>
  )
}

export default Clients
